import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';

export interface EmployeeIdModalData {
  // Prefilled (and preselected) value — the logged-in account's own Mitarbeiternummer.
  employeeId: string;
}

// Accounts are shared between several people, so whoever creates an Auftragsbegleitschein or a
// Lieferzettel, or checks Lieferzettel positions ("WA Überprüfung"), confirms their own
// Mitarbeiternummer first. Resolves with the confirmed number, or null on Abbrechen. Only closes on
// Übernehmen once the backend confirms the user exists.
@Component({
  selector: 'app-employee-id-modal',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './employee-id-modal.component.html',
  styleUrls: ['./employee-id-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeeIdModalComponent {
  private readonly employeeIdInput = viewChild.required<ElementRef<HTMLInputElement>>('employeeIdInput');

  public readonly form: FormGroup<{ employeeId: FormControl<string> }>;
  public readonly isChecking = signal(false);
  // Error from the backend check ("Die Benutzerkennung existiert nicht"); cleared as soon as the user types.
  public readonly serverError = signal<string | null>(null);

  constructor(
    @Inject('MODAL_DATA') data: EmployeeIdModalData | null,
    @Inject('MODAL_CLOSE') private modalClose: (result: string | null) => void,
    fb: FormBuilder,
    private userService: UserService,
    private destroyRef: DestroyRef
  ) {
    this.form = fb.nonNullable.group({
      employeeId: [data?.employeeId ?? '', [Validators.required, Validators.pattern(/^\d+$/)]],
    });

    this.form.controls.employeeId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.serverError.set(null));

    // Focus with the prefilled value selected: typing replaces it, Enter keeps it.
    afterNextRender(() => this.focusInput());
  }

  public get isInvalid(): boolean {
    const control = this.form.controls.employeeId;
    return (control.invalid && control.touched) || !!this.serverError();
  }

  public get errorText(): string | null {
    const control = this.form.controls.employeeId;
    if (this.serverError()) return this.serverError();
    if (!control.touched) return null;
    if (control.hasError('required')) return 'Bitte geben Sie Ihre Mitarbeiternummer ein';
    if (control.hasError('pattern')) return 'Die Mitarbeiternummer darf nur Ziffern enthalten';
    return null;
  }

  public confirm(): void {
    if (this.isChecking()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const employeeId = this.form.getRawValue().employeeId;
    this.isChecking.set(true);
    this.userService
      .checkEmployeeIdExists(employeeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.modalClose(String(response.DATA.employeeId)),
        error: (error: HttpErrorResponse) => {
          this.isChecking.set(false);
          // 400 only happens for a number the backend rejects outright (e.g. 0) — same outcome for the user.
          const notFound = error.status === 404 || error.status === 400;
          this.serverError.set(notFound ? 'Die Benutzerkennung existiert nicht' : 'Die Mitarbeiternummer konnte nicht geprüft werden');
          this.focusInput();
        },
      });
  }

  public close(): void {
    this.modalClose(null);
  }

  @HostListener('document:keydown.escape', ['$event'])
  public handleEscape(event: Event): void {
    event.preventDefault();
    this.close();
  }

  private focusInput(): void {
    const input = this.employeeIdInput().nativeElement;
    input.focus();
    input.select();
  }
}
